import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const WIDTH = 1080;
const HEIGHT = 1080;
const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicPreviewPoster({
  graphicData,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = 'Bebas Neue',
  heroFontSize = null,
  accentBlockColor = null,
  dominantImageScale = null,
  dominantImageX = null,
  dominantImageY = null,
  dominantImageOpacity = 100,
  detailTextColor = null,
  teamSport = 'Baseball',
  dateFormat = 'full',
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;
  const primary = teamColors.primary;
  const secondary = teamColors.secondary;
  const hasImage = Boolean(dominantImage);
  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;
  const accentColor = accentBlockColor || secondary;
  const scale = (dominantImageScale ?? 100) / 100;
  const displayHeroFontSize = heroFontSize != null ? Number(heroFontSize) : 160;
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const barlow = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${primary} 0%, ${secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${primary} 0%, ${secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${primary} 50%, ${secondary} 50%)`;
      case 'solid-primary':
        return primary;
      case 'solid-secondary':
        return secondary;
      default:
        return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
    }
  };

  return (
    <div
      id="graphic-preview"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        background: getBackground(),
        fontFamily: heroFontFamily,
      }}
    >
      {/* Layer 1 — Background photo */}
      {backgroundPhoto && (
        <img
          src={backgroundPhoto}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: `${bgImageY}%`,
            left: `${bgImageX}%`,
            transform: `translate(-50%, -50%) scale(${bgImageScale})`,
            transformOrigin: 'center center',
            width: 'auto',
            height: 'auto',
            minWidth: '100%',
            minHeight: '100%',
            maxWidth: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* Layer 2 — Directional scrim, left lighter to right darker */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT,
          background: 'linear-gradient(to right, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.72) 100%)',
          zIndex: 2,
        }}
      />

      {/* Layer 3 — Dominant image, bottom-left anchor by default */}
      {hasImage && (
        <img
          src={dominantImage}
          alt=""
          style={{
            position: 'absolute',
            top: `${dominantImageY ?? 100}%`,
            left: `${dominantImageX ?? 0}%`,
            transform: `translate(-50%, -50%) scale(${scale})`,
            opacity: (dominantImageOpacity ?? 100) / 100,
            height: HEIGHT * scale,
            width: 'auto',
            maxWidth: 'none',
            zIndex: 3,
          }}
        />
      )}

      {/* Layer 4b — Thin decorative rule, right-aligned */}
      <div
        style={{
          position: 'absolute',
          top: 134,
          right: 60,
          width: 160,
          height: 4,
          background: accentColor,
          zIndex: 4,
        }}
      />

      {/* Layer 5 — Logo stack, right-aligned: home logo, home name, VS, away logo, away name */}
      {homeTeamLogo ? (
        <img
          src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
          alt={homeTeam}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 170,
            right: 60,
            height: 80,
            width: 'auto',
            maxWidth: 120,
            zIndex: 5,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 170,
            right: 60,
            fontSize: 72,
            lineHeight: 1,
            zIndex: 5,
          }}
        >
          {teamSport === 'Softball' ? '🥎' : '⚾'}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: 256,
          right: 60,
          fontFamily: heroFontFamily,
          fontSize: 36,
          lineHeight: 1,
          color: detailTextColor || '#ffffff',
          textShadow: '0 2px 8px rgba(0,0,0,0.6)',
          zIndex: 5,
        }}
      >
        {homeTeam || 'YOUR TEAM'}
      </div>
      <div
        style={{
          position: 'absolute',
          top: 306,
          right: 60,
          fontFamily: heroFontFamily,
          fontSize: 48,
          lineHeight: 1,
          color: '#ffffff',
          textShadow: '0 2px 12px rgba(0,0,0,0.6)',
          zIndex: 5,
        }}
      >
        VS
      </div>
      {awayTeamLogoPreview ? (
        <img
          src={awayTeamLogoPreview}
          alt={awayTeam}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 368,
            right: 60,
            height: 80,
            width: 'auto',
            maxWidth: 120,
            zIndex: 5,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 368,
            right: 60,
            fontSize: 72,
            lineHeight: 1,
            zIndex: 5,
          }}
        >
          {teamSport === 'Softball' ? '🥎' : '⚾'}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: 454,
          right: 60,
          fontFamily: heroFontFamily,
          fontSize: 36,
          lineHeight: 1,
          color: detailTextColor || '#ffffff',
          textShadow: '0 2px 8px rgba(0,0,0,0.6)',
          zIndex: 5,
        }}
      >
        {awayTeam || 'OPPONENT'}
      </div>

      {/* Layer 6a — "GAME" large hero text, right side */}
      <div
        style={{
          position: 'absolute',
          top: 580,
          right: 60,
          fontFamily: heroFontFamily,
          fontSize: displayHeroFontSize,
          lineHeight: 1,
          color: '#ffffff',
          letterSpacing: 6,
          textShadow: '0 4px 40px rgba(0,0,0,0.75)',
          zIndex: 6,
        }}
      >
        GAME
      </div>

      {/* Layer 6b — "DAY", accent color, offset 20px further right */}
      <div
        style={{
          position: 'absolute',
          top: 580 + displayHeroFontSize,
          right: 40,
          fontFamily: heroFontFamily,
          fontSize: displayHeroFontSize,
          lineHeight: 1,
          color: accentColor,
          letterSpacing: 6,
          textShadow: '0 4px 40px rgba(0,0,0,0.75)',
          zIndex: 6,
        }}
      >
        DAY
      </div>

      {/* Layer 7 — Game details, right-aligned bottom */}
      <div
        style={{
          position: 'absolute',
          top: 900,
          left: 500,
          right: 60,
          textAlign: 'right',
          fontFamily: barlow,
          fontSize: 38,
          fontWeight: 600,
          lineHeight: 1.4,
          color: detailTextColor || 'rgba(255,255,255,0.85)',
          zIndex: 7,
        }}
      >
        {formatGameDate(gameDate, dateFormat) && <div>{formatGameDate(gameDate, dateFormat)}</div>}
        {gameTime && <div>{formatGameTime(gameTime)}</div>}
        {location && <div>{location}</div>}
      </div>

      {/* Layer 8 — Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 8,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicPreviewPoster;
